import {html, render} from "lit";
import {closeOtherPopovers} from "../components/_inputs/combobox-utils/setup-outside-click-handler";
import {Observable} from "rxjs";
import {container} from "tsyringe";
import {BulkCommand} from "../commands/bulk.command";
import {ICommand} from "../commands/i-command.interface";
import {HaConfirmComponent} from "../components/_dialogs/ha-confirm/ha-confirm.component";
import {AbstractEntitiesDialogComponent} from "../components/ha-dialog/abstract-entities-dialog.component";
import {AbstractEntityDialogComponent} from "../components/ha-dialog/abstract-entity-dialog.component";
import {DialogCommandName} from "../components/ha-dialog/dialog-command.enum";
import {DialogCommand} from "../components/ha-dialog/dialog-command.interface";
import {DialogOptions} from "../components/ha-dialog/dialog-options.interface";
import {HaPopoverComponent} from "../components/ha-popover/ha-popover.component";
import {Entity} from "../domain/entities/entity";
import {CommandBus} from "../services/command-bus.service";
import {ClassOf} from "../types/class-of.type";
import {ToastMessage} from "../types/toast-message.type";
import {notificationIcon} from "./notification-icons";
import {HtmlHelper} from "./html.helper";
import {PointerPositionHelper} from "./pointer-position.helper";
import {ConfirmDialogOptions} from "./interfaces/confirm-dialog-options.interface";
import {DialogChoice, dialogCommandToChoice} from "./dialog-choice.helper";
import {StringHelper} from "./string.helper";
import {ArrayHelper} from "./array.helper";
import {ClassInfo} from "lit-html/directives/class-map.js";
import type {PopoverListItem, ItemClickedDetail} from "@o2d/popover-list";
import {generateShortId} from "@o2d/shared";
import type {O2dPopoverListElement} from "@o2d/popover-list";
// Side-effect import: registers the o2d-popover-list custom element
import "@o2d/popover-list";


export class UiHelper {

    private static openedPopOver: HaPopoverComponent | null = null;

    public static async confirm(confirmOptions: ConfirmDialogOptions): Promise<boolean> {
        return (await UiHelper._openConfirm(confirmOptions, false)) === 'confirm';
    }

    /**
     * Like {@link confirm}, but tells the two buttons apart from closing the dialog: resolves
     * `'confirm'` (primary button), `'cancel'` (secondary button) or `null` (closed without
     * choosing). Used where "No" is an answer of its own, e.g. the whiteboard's "Als Bild oder
     * nur den Text?" question (Anke, 2026-10-02).
     *
     * It closes two ways besides its buttons: the close button in its header, and Esc. A press
     * "outside" cannot happen — the dialog's own backdrop covers the whole window (measured
     * 2026-10-02), so it is modal in effect. While it is open, no `keydown` reaches the page
     * below: a capturing `keydown` listener on `document` stops every `keydown`, wherever the
     * focus sits (a click on the backdrop moves it to `body`). Otherwise Delete, Ctrl+Z or the
     * arrow keys would reach the window-level shortcuts of the board. Note for callers: this also
     * keeps `keydown` from listeners inside `content` (default actions such as typing still
     * happen) — fine for plain text, not for content with its own key handling.
     */
    public static choose(confirmOptions: ConfirmDialogOptions): Promise<DialogChoice> {
        return UiHelper._openConfirm(confirmOptions, true);
    }

    private static _openConfirm(confirmOptions: ConfirmDialogOptions, closable: boolean): Promise<DialogChoice> {
        const promise = new Promise<DialogChoice>(resolve => {
            const haConfirmElement = document.createElement(`ha-confirm`) as HaConfirmComponent;
            haConfirmElement.content = confirmOptions.content;
            haConfirmElement.confirmLabel = confirmOptions.confirmLabel;
            if (closable) {
                haConfirmElement.closable = true;
            }

            if (confirmOptions.title) {
                haConfirmElement.title = confirmOptions.title;
            }

            if (confirmOptions.cancelLabel) {
                haConfirmElement.cancelLabel = confirmOptions.cancelLabel;
            }

            // One way out for every exit (buttons, close button, Esc): remove all listeners,
            // remove the element, resolve exactly once.
            let settled = false;
            const finish = (command: unknown): void => {
                if (settled) {
                    return;
                }
                settled = true;
                haConfirmElement.removeEventListener(`dialog.message`, listener);
                if (closable) {
                    document.removeEventListener(`keydown`, onKeyDown, {capture: true});
                }
                HtmlHelper.removeElement(haConfirmElement);
                resolve(dialogCommandToChoice(command));
            };
            const listener = {
                handleEvent: (evt: CustomEvent<{ value: DialogCommand<void> }>) => finish(evt.detail.value.command)
            };
            const onKeyDown = (evt: Event): void => {
                // Removed from the page without a `dialog.message` (e.g. by other code): let go
                // instead of swallowing every key of the page for good.
                if (!haConfirmElement.isConnected) {
                    finish(DialogCommandName.CLOSE);
                    return;
                }
                evt.stopPropagation();
                if ((evt as KeyboardEvent).key === `Escape`) {
                    finish(DialogCommandName.CLOSE);
                }
            };

            haConfirmElement.addEventListener(`dialog.message`, listener);

            try {
                document.body.append(haConfirmElement);

                if (typeof haConfirmElement.open === 'function') {
                    haConfirmElement.open();
                }
            } catch (e) {
                // Never leave a half-open dialog (or its listeners) behind: closed, `null`.
                console.error(e);
                finish(DialogCommandName.CLOSE);
                return;
            }

            if (closable) {
                // Registered only once the dialog is open, so a failed open cannot leave it behind.
                // Capture on `document`: runs before any bubbling listener (the board's sit on
                // `window`), whether the focus is in the dialog or on `body`. `stopPropagation`
                // does not prevent default actions, so Tab, Enter and Space still work on the
                // dialog's own buttons.
                document.addEventListener(`keydown`, onKeyDown, {capture: true});
            }
        });

        return promise.catch(e => {
            console.error(e);
            return null;
        });
    }

    public static notify(toastMessage: ToastMessage): void {

        const notificationElement = document.createElement(`dialog`);
        notificationElement.classList.add(`notification`, `notification-type__${toastMessage.level.toLocaleLowerCase()}`)
        render(html`
            <header>${toastMessage.title}</header>
            <main>
                <div class="notification__icon"><i class="icon ${notificationIcon(toastMessage.level)}"></i></div>
                <div class="notification__content">${toastMessage.content}</div>
            </main>
            <footer></footer>
        `, notificationElement);

        document.body.append(notificationElement);
        // Open via the attribute, not show(): show() moves focus into the dialog, so a user who
        // saves and keeps typing would lose the input to the toast. The toast is not modal and
        // is removed via HtmlHelper.removeElement, so nothing else depends on show().
        notificationElement.setAttribute('open', '');

        if (toastMessage.duration) {
            setTimeout(() => HtmlHelper.removeElement(notificationElement), toastMessage.duration);
        }
    }

    /**
     * Displays a context menu at the specified target element using the Popover API.
     * Uses CSS Anchor Positioning for placement and light-dismiss for auto-close.
     *
     * @param target - The element to anchor the popover to.
     * @param items - The menu items to display.
     * @returns The selected item, or null if dismissed without selection.
     */
    public static async showContextMenu(target: HTMLElement, items: PopoverListItem[]): Promise<PopoverListItem | null> {
        const popover = document.createElement('o2d-popover-list') as O2dPopoverListElement;
        popover.items = items;
        popover.id = `ctx-${generateShortId()}`;
        popover.setAttribute('popover', 'auto');

        // CSS Anchor Positioning (properties not yet in TS lib types)
        const anchorName = `--${popover.id}`;
        target.style.setProperty('anchor-name', anchorName);
        popover.style.setProperty('position-anchor', anchorName);

        document.body.append(popover);
        closeOtherPopovers(popover);
        popover.showPopover();

        return new Promise<PopoverListItem | null>(resolve => {
            // o2d-popover-list dispatches 'item-clicked' as CustomEvent<ItemClickedDetail>
            // (fireEvent<ItemClickedDetail> in the element), but its published typings declare no
            // HTMLElementEventMap entry for it, so the listener receives a plain Event type.
            popover.addEventListener('item-clicked', (e: Event) => {
                resolve((e as CustomEvent<ItemClickedDetail>).detail.item);
                popover.hidePopover();
            }, {once: true});

            popover.addEventListener('toggle', (e: ToggleEvent) => {
                if (e.newState === 'closed') {
                    resolve(null);
                    // Only remove anchor-name if it still belongs to this popover
                    if (target.style.getPropertyValue('anchor-name') === anchorName) {
                        target.style.removeProperty('anchor-name');
                    }
                    popover.remove();
                }
            });
        });
    }

    /**
     * Opens a user-provided URL (e.g. Project.url) in a new tab.
     *
     * Two hardenings over a bare `window.open(url, '_blank')`:
     * - Scheme allowlist (http/https only): the URL comes from free-text user input with
     *   no server-side scheme validation on save, so a `javascript:`/`data:` URI stored
     *   there must not reach `window.open` unfiltered.
     * - Explicit `noopener,noreferrer`: `window.open` (unlike an `<a target="_blank">`
     *   element) does NOT get an implicit `noopener` from modern browsers, so the opened
     *   page would otherwise keep a `window.opener` handle back to this app and could
     *   reverse-tabnab it (`opener.location = ...`).
     *
     * Silently no-ops on a missing/invalid/non-http(s) URL — callers already gate the
     * triggering UI (e.g. a disabled menu item) on the URL being present.
     */
    public static openExternalLink(url: string | null | undefined): void {
        if (!url) {
            return;
        }

        let parsed: URL;
        try {
            parsed = new URL(url);
        } catch {
            return;
        }

        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return;
        }

        window.open(parsed.toString(), '_blank', 'noopener,noreferrer');
    }

    public static openDialogEntitiesEdit<T extends Entity>(entityClass: ClassOf<T>, entities: T[], dialogOptions: DialogOptions, selectedEntity: T | null = null): Observable<BulkCommand<T> | null> {

        return new Observable<BulkCommand<T> | null>(subscriber => {
            entities = Array.from(entities);
            const entityClassName = StringHelper.toKebabCase(entityClass.name);

            const dialog = document.createElement(`ha-dialog-${entityClassName}-edit`) as AbstractEntitiesDialogComponent<T>;
            dialog.maximizable = dialogOptions.maximizable ?? false;
            dialog.minimizable = dialogOptions.minimizable ?? false;
            dialog.closable = dialogOptions.closable;

            if (dialogOptions.title) {
                dialog.title = dialogOptions.title;
            }

            if (entities.length > 0) {
                dialog.entities = entities;
                dialog.selectedEntity = selectedEntity ?? ArrayHelper.first(entities)!;
            }

            if (dialogOptions.template) {
                dialog.template = dialogOptions.template;
            }

            const listener = {
                handleEvent: async (evt: CustomEvent<{ value: DialogCommand<BulkCommand<T>> }>) => {
                    switch (evt.detail.value.command) {
                        case DialogCommandName.OKAY:
                            subscriber.next(evt.detail.value.payload);
                            break;
                        case DialogCommandName.CLOSE:
                        case DialogCommandName.CANCEL:
                            dialog.removeEventListener(`dialog.message`, listener);
                            await dialog.close();
                            dialog.parentNode?.removeChild(dialog);
                            subscriber.complete();
                            return;
                    }
                }
            };

            dialog.addEventListener(`dialog.message`, listener);
            document.body.append(dialog);
            dialog.open();
        });
    }

    public static openDialogEntityEdit<T extends ICommand>(entityClass: ClassOf<Entity>, entity: Entity | null, dialogOptions: DialogOptions): /* Observable<T | null> */ void {

        try {
            const entityClassName = StringHelper.toKebabCase(entityClass.name);
            const dialog = document.createElement(`ha-dialog-${entityClassName}-edit`) as AbstractEntityDialogComponent<Entity>;
            dialog.maximizable = dialogOptions.maximizable ?? false;
            dialog.minimizable = dialogOptions.minimizable ?? false;
            dialog.closable = dialogOptions.closable;
            dialog.creationMode = dialogOptions.creationMode ? entityClass : null;

            if ('template' in dialog) {
                dialog.template = entity;
            }

            if ('entity' in dialog) {
                dialog.entity = entity;
            }

            // Set entity via entity-specific property name (e.g., 'project', 'daily')
            // for dialogs that use their own property instead of AbstractEntityDialogComponent's 'entity'
            const entityPropertyName = entityClassName.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
            if (entityPropertyName in dialog) {
                (dialog as any)[entityPropertyName] = entity;
            }

            if (dialogOptions.title) {
                dialog.title = dialogOptions.title;
            }
            const listener = {
                handleEvent: (evt: CustomEvent<{ value: DialogCommand<T> }>) => {
                    switch (evt.detail.value.command) {
                        case DialogCommandName.OKAY:
                            container.resolve(CommandBus).dispatchCommand(evt.detail.value.payload).then();
                            // subscriber.next(evt.detail.value.payload);
                            break;
                        case DialogCommandName.CLOSE:
                        case DialogCommandName.CANCEL:
                            dialog.removeEventListener(`dialog.message`, listener);
                            dialog.parentNode?.removeChild(dialog);
                            // subscriber.complete();
                            return;
                    }
                }
            };
            dialog.addEventListener(`dialog.message`, listener);
            document.body.append(dialog);

            if (typeof dialog.open === 'function') {
                if (dialog.creationMode && !dialog.entity) {
                    dialog.createNewEntity().then(() => dialog.open());
                } else {
                    dialog.open();
                }
            }
        } catch (err: any) {
            // subscriber.error(err);
            throw new Error(err.message);
        }
    }

    /**
     * Shows `content` in the one global `ha-popover`, replacing any popover that is
     * open; `null` closes it. Returns the new popover element, or `null` when nothing
     * is open afterwards — a caller that keeps state for its popover (the image
     * preview's key listener) checks `isConnected` on it to learn whether its popover
     * is still the open one.
     */
    public static popover(content: unknown, cssClasses: ClassInfo | null = null): HaPopoverComponent | null {
        if (UiHelper.openedPopOver) {
            HtmlHelper.removeElement(UiHelper.openedPopOver);
            // Drop the reference too, not just the element: without this the
            // field keeps pointing at a detached node for as long as no new
            // popover is opened.
            UiHelper.openedPopOver = null;
        }

        if (!content) {
            return null;
        }

        const popover = document.createElement(`ha-popover`) as HaPopoverComponent;
        popover.content = content;

        if (cssClasses) {
            popover.classList.add(...Object.entries(cssClasses).filter(([key, value]) => !!value).map(([key, _]) => key));
        }

        // The popover's stylesheet reads the pointer position as its initial position
        // (`dialogs.css`, `ha-popover`), so it is written onto <html> here, once per opened
        // popover — not on every mouse move (see PointerPositionHelper).
        if (PointerPositionHelper.x !== null && PointerPositionHelper.y !== null) {
            HtmlHelper.setGlobalVar(`mouse-x`, `${PointerPositionHelper.x}px`);
            HtmlHelper.setGlobalVar(`mouse-y`, `${PointerPositionHelper.y}px`);
        }

        document.body.append(popover);
        UiHelper.openedPopOver = popover;
        return popover;
    }

}